import React, { useEffect, useMemo, useState } from 'react';
import { FiAward, FiCalendar, FiCheck, FiEdit2, FiFlag, FiList, FiPlus, FiRefreshCw, FiSearch, FiUsers, FiX } from 'react-icons/fi';
import api from '../services/api';

const emptyTeam = { name: '', countryCode: '', flagEmoji: '', flagCode: '', flagUrl: '', groupName: '', isActive: true, sortOrder: 0 };
const emptyMatch = { matchDate: '', matchTime: '12:00', teamAId: '', teamBId: '', teamARewardPerPoint: 0, teamBRewardPerPoint: 0, status: 'open' };

function AdminFootballPage() {
  const [activeTab, setActiveTab] = useState('matches');
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [allPredictions, setAllPredictions] = useState([]);
  const [predictionSearch, setPredictionSearch] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [teamForm, setTeamForm] = useState(emptyTeam);
  const [matchForm, setMatchForm] = useState(emptyMatch);
  const [editMatch, setEditMatch] = useState(null);
  const [editMatchForm, setEditMatchForm] = useState(emptyMatch);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [settleTeamId, setSettleTeamId] = useState('');
  const [claimUpdatingIds, setClaimUpdatingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const selectedMatch = useMemo(() => matches.find(match => match.id === Number(selectedMatchId)), [matches, selectedMatchId]);

  const filteredAllPredictions = useMemo(() => {
    const keyword = predictionSearch.trim().toLowerCase();
    if (!keyword) return allPredictions;
    return allPredictions.filter(row => {
      const text = [
        row.playerName,
        row.nationalId,
        row.phone,
        row.selectedTeam?.name,
        row.winnerTeam?.name,
        row.teamA?.name,
        row.teamB?.name,
        row.prizeName,
        row.isRewardClaimed ? '已領取' : '未領取',
        resultText(row.resultStatus),
        statusText(row.matchStatus),
        new Date(row.matchAt).toLocaleString('zh-TW'),
        new Date(row.createdAt).toLocaleString('zh-TW'),
      ].filter(Boolean).join(' ').toLowerCase();
      return text.includes(keyword);
    });
  }, [allPredictions, predictionSearch]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [teamData, matchData] = await Promise.all([api.getFootballTeams(), api.getFootballAdminMatches()]);
      setTeams(teamData);
      setMatches(matchData);
      setSelectedMatchId(prev => prev || (matchData[0]?.id ? String(matchData[0].id) : ''));
    } catch (err) {
      setMsg(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const loadAllPredictions = async () => {
    try {
      setAllPredictions(await api.getAllFootballPredictions());
    } catch (err) {
      setMsg(err.message || '載入所有預測明細失敗');
    }
  };

  useEffect(() => {
    if (activeTab === 'predictionDetails') loadAllPredictions();
  }, [activeTab]);

  useEffect(() => {
    if (!selectedMatchId) {
      setPredictions([]);
      return;
    }
    api.getFootballPredictions(selectedMatchId).then(setPredictions).catch(err => setMsg(err.message || '載入預測紀錄失敗'));
  }, [selectedMatchId]);

  const saveNewMatch = async (event) => {
    event.preventDefault();
    try {
      await api.createFootballMatch(toMatchPayload(matchForm));
      setMatchForm(emptyMatch);
      setMsg('賽事已新增');
      await loadAll();
      if (activeTab === 'predictionDetails') await loadAllPredictions();
    } catch (err) {
      setMsg(err.message || '新增賽事失敗');
    }
  };

  const openEditMatch = (match) => {
    setEditMatch(match);
    setEditMatchForm(fromMatch(match));
  };

  const saveEditMatch = async (event) => {
    event.preventDefault();
    if (!editMatch) return;
    try {
      await api.updateFootballMatch(editMatch.id, toMatchPayload(editMatchForm));
      setEditMatch(null);
      setMsg('賽事已更新');
      await loadAll();
      if (activeTab === 'predictionDetails') await loadAllPredictions();
    } catch (err) {
      setMsg(err.message || '更新賽事失敗');
    }
  };

  const saveTeam = async (event) => {
    event.preventDefault();
    try {
      if (editingTeamId) await api.updateFootballTeam(editingTeamId, teamForm);
      else await api.createFootballTeam(teamForm);
      setTeamForm(emptyTeam);
      setEditingTeamId(null);
      setMsg(editingTeamId ? '隊伍已更新' : '隊伍已新增');
      await loadAll();
    } catch (err) {
      setMsg(err.message || '儲存隊伍失敗');
    }
  };

  const editTeam = (team) => {
    setEditingTeamId(team.id);
    setTeamForm({
      name: team.name,
      countryCode: team.countryCode,
      flagEmoji: team.flagEmoji || '',
      flagCode: team.flagCode || '',
      flagUrl: team.flagUrl || '',
      groupName: team.groupName || '',
      isActive: team.isActive,
      sortOrder: team.sortOrder || 0,
    });
    setActiveTab('teams');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const settleMatch = async () => {
    if (!selectedMatch || !settleTeamId) {
      setMsg('請先選擇賽事與勝利隊伍');
      return;
    }
    if (!window.confirm('確定結算此場賽事？結算後會更新所有預測結果。')) return;
    try {
      await api.settleFootballMatch(selectedMatch.id, settleTeamId);
      setMsg('賽事已結算');
      setSettleTeamId('');
      await loadAll();
      setPredictions(await api.getFootballPredictions(selectedMatch.id));
      await loadAllPredictions();
    } catch (err) {
      setMsg(err.message || '結算失敗');
    }
  };

  const toggleRewardClaimed = async (row) => {
    if (claimUpdatingIds.includes(row.id)) return;
    const nextValue = !row.isRewardClaimed;
    setClaimUpdatingIds(ids => [...ids, row.id]);
    setAllPredictions(current => current.map(item => item.id === row.id ? {
      ...item,
      isRewardClaimed: nextValue,
      rewardClaimedAt: nextValue ? new Date().toISOString() : null,
    } : item));
    try {
      const result = await api.updateFootballPredictionRewardClaimed(row.id, nextValue);
      setAllPredictions(current => current.map(item => item.id === row.id ? {
        ...item,
        isRewardClaimed: result.isRewardClaimed,
        rewardClaimedAt: result.rewardClaimedAt,
      } : item));
      setMsg(nextValue ? '已標記為已領取獎勵' : '已取消領取標記');
    } catch (err) {
      setAllPredictions(current => current.map(item => item.id === row.id ? row : item));
      setMsg(err.message || '更新領取標記失敗');
    } finally {
      setClaimUpdatingIds(ids => ids.filter(id => id !== row.id));
    }
  };

  return (
    <div className="admin-page football-admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title"><FiAward style={{ marginRight: 8, verticalAlign: 'middle' }} />足球世界盃預測</h1>
        <button type="button" className="admin-btn-secondary" onClick={loadAll}><FiRefreshCw style={{ marginRight: 6 }} />重新整理</button>
      </div>

      <div className="football-admin-tabs">
        <TabButton active={activeTab === 'matches'} onClick={() => setActiveTab('matches')} icon={<FiCalendar />} label="新增賽事" />
        <TabButton active={activeTab === 'teams'} onClick={() => setActiveTab('teams')} icon={<FiFlag />} label="編輯隊伍" />
        <TabButton active={activeTab === 'settlement'} onClick={() => setActiveTab('settlement')} icon={<FiCheck />} label="結算" />
        <TabButton active={activeTab === 'predictionDetails'} onClick={() => setActiveTab('predictionDetails')} icon={<FiUsers />} label="預測明細" />
      </div>

      {msg && <div className="football-admin-msg">{msg}</div>}
      {loading ? <div className="admin-loading">載入中...</div> : null}

      {activeTab === 'matches' && <MatchesTab teams={teams} matches={matches} matchForm={matchForm} setMatchForm={setMatchForm} saveNewMatch={saveNewMatch} openEditMatch={openEditMatch} />}
      {activeTab === 'teams' && <TeamsTab teams={teams} teamForm={teamForm} setTeamForm={setTeamForm} editingTeamId={editingTeamId} setEditingTeamId={setEditingTeamId} saveTeam={saveTeam} editTeam={editTeam} />}
      {activeTab === 'settlement' && <SettlementTab matches={matches} predictions={predictions} selectedMatchId={selectedMatchId} setSelectedMatchId={setSelectedMatchId} selectedMatch={selectedMatch} settleTeamId={settleTeamId} setSettleTeamId={setSettleTeamId} settleMatch={settleMatch} />}
      {activeTab === 'predictionDetails' && <PredictionDetailsTab rows={filteredAllPredictions} total={allPredictions.length} query={predictionSearch} setQuery={setPredictionSearch} refresh={loadAllPredictions} onToggleClaimed={toggleRewardClaimed} updatingIds={claimUpdatingIds} />}

      {editMatch && <MatchEditModal teams={teams} form={editMatchForm} setForm={setEditMatchForm} onClose={() => setEditMatch(null)} onSubmit={saveEditMatch} />}
    </div>
  );
}

function PredictionDetailsTab({ rows, total, query, setQuery, refresh, onToggleClaimed, updatingIds }) {
  return (
    <section className="football-admin-panel wide">
      <div className="football-detail-head">
        <h2><FiUsers />所有預測用戶明細</h2>
        <button type="button" className="admin-btn-secondary" onClick={refresh}><FiRefreshCw />重新整理</button>
      </div>
      <div className="football-detail-toolbar">
        <div className="football-detail-search">
          <FiSearch />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="模糊搜尋姓名、身分證、手機、隊伍、結果、賽事時間" />
          {query && <button type="button" onClick={() => setQuery('')}><FiX /></button>}
        </div>
        <div className="football-detail-count">顯示 {rows.length} / {total} 筆</div>
      </div>
      <div className="admin-table-wrap football-detail-table-wrap">
        <table className="admin-table football-detail-table">
          <thead><tr><th>預測時間</th><th>用戶</th><th>手機</th><th>身分證</th><th>賽事</th><th>預測隊伍</th><th>勝利隊伍</th><th>結果</th><th>獎項</th><th>優惠金</th><th>領取狀態</th></tr></thead>
          <tbody>
            {rows.map(row => <tr key={row.id}><td>{new Date(row.createdAt).toLocaleString('zh-TW')}</td><td>{row.playerName}</td><td>{row.phone}</td><td>{row.nationalId}</td><td><div className="football-match-cell"><small>{new Date(row.matchAt).toLocaleString('zh-TW')}</small><span><TeamName team={row.teamA} /> <b>VS</b> <TeamName team={row.teamB} /></span></div></td><td><TeamName team={row.selectedTeam} /></td><td>{row.winnerTeam ? <TeamName team={row.winnerTeam} /> : '-'}</td><td><span className={`football-result-badge ${row.resultStatus}`}>{resultText(row.resultStatus)}</span></td><td>{row.prizeName || '-'}</td><td>{money(row.rewardAmount)}</td><td><RewardClaimButton row={row} disabled={updatingIds.includes(row.id)} onToggle={() => onToggleClaimed(row)} /></td></tr>)}
            {rows.length === 0 && <tr><td colSpan="11" className="football-empty-row">沒有符合條件的預測明細</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RewardClaimButton({ row, disabled, onToggle }) {
  if (row.resultStatus !== 'won') {
    return <span className="football-claim-na">無可領取</span>;
  }

  return (
    <button type="button" className={`football-claim-btn${row.isRewardClaimed ? ' claimed' : ''}`} onClick={onToggle} disabled={disabled} title={row.rewardClaimedAt ? `標記時間：${new Date(row.rewardClaimedAt).toLocaleString('zh-TW')}` : '尚未標記領取'}>
      <span>{row.isRewardClaimed ? '已領取' : '未領取'}</span>
      {row.rewardClaimedAt && <small>{new Date(row.rewardClaimedAt).toLocaleString('zh-TW')}</small>}
    </button>
  );
}

function TabButton({ active, onClick, icon, label }) {
  return <button type="button" className={`football-admin-tab${active ? ' active' : ''}`} onClick={onClick}>{icon}{label}</button>;
}

function MatchesTab({ teams, matches, matchForm, setMatchForm, saveNewMatch, openEditMatch }) {
  return (
    <>
      <form className="football-admin-panel" onSubmit={saveNewMatch}>
        <h2><FiCalendar />新增賽事</h2>
        <MatchFields teams={teams} form={matchForm} setForm={setMatchForm} />
        <div className="football-admin-actions">
          <button className="admin-btn-primary" type="submit"><FiPlus />新增賽事</button>
        </div>
      </form>

      <section className="football-admin-panel wide">
        <h2><FiList />賽事列表</h2>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>時間</th><th>A 隊</th><th>B 隊</th><th>預測數</th><th>狀態</th><th>操作</th></tr></thead>
            <tbody>{matches.map(match => <tr key={match.id}><td>{new Date(match.matchAt).toLocaleString('zh-TW')}</td><td><TeamName team={match.teamA} /> / {money(match.teamARewardPerPoint)}</td><td><TeamName team={match.teamB} /> / {money(match.teamBRewardPerPoint)}</td><td>{match.predictionCount}</td><td><span className={`status-badge ${match.status === 'open' ? 'on' : 'off'}`}>{statusText(match.status)}</span></td><td className="td-actions"><button type="button" className="admin-btn-sm" onClick={() => openEditMatch(match)} title="編輯賽事"><FiEdit2 /></button></td></tr>)}</tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function MatchEditModal({ teams, form, setForm, onClose, onSubmit }) {
  return (
    <div className="admin-modal-overlay" onMouseDown={onClose}>
      <div className="admin-modal football-edit-modal" onMouseDown={event => event.stopPropagation()}>
        <div className="football-modal-head">
          <h2><FiEdit2 />編輯賽事</h2>
          <button type="button" className="football-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <form onSubmit={onSubmit}>
          <MatchFields teams={teams} form={form} setForm={setForm} />
          <div className="admin-modal-actions">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>取消</button>
            <button type="submit" className="admin-btn-primary">儲存賽事</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MatchFields({ teams, form, setForm }) {
  return (
    <div className="admin-form-grid">
      <div className="admin-form-group"><label>比賽日期</label><input type="date" value={form.matchDate} onChange={e => setForm({ ...form, matchDate: e.target.value })} /></div>
      <div className="admin-form-group"><label>比賽時間</label><input type="time" value={form.matchTime} onChange={e => setForm({ ...form, matchTime: e.target.value })} /></div>
      <TeamSearchSelect label="A 隊" value={form.teamAId} teams={teams} onChange={value => setForm({ ...form, teamAId: value })} />
      <TeamSearchSelect label="B 隊" value={form.teamBId} teams={teams} onChange={value => setForm({ ...form, teamBId: value })} />
      <div className="admin-form-group"><label>A 隊優惠金</label><input type="number" min="0" value={form.teamARewardPerPoint} onChange={e => setForm({ ...form, teamARewardPerPoint: e.target.value })} /></div>
      <div className="admin-form-group"><label>B 隊優惠金</label><input type="number" min="0" value={form.teamBRewardPerPoint} onChange={e => setForm({ ...form, teamBRewardPerPoint: e.target.value })} /></div>
      <div className="admin-form-group full"><label>狀態</label><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option value="open">開放預測</option><option value="closed">關閉預測</option><option value="settled">已結算</option></select></div>
    </div>
  );
}

function TeamSearchSelect({ label, value, teams, onChange }) {
  const selectedTeam = teams.find(team => team.id === Number(value));
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (selectedTeam) {
      setDirty(false);
      setQuery(`${selectedTeam.groupName || ''} ${selectedTeam.name}`.trim());
      return;
    }
    if (!dirty) setQuery('');
  }, [selectedTeam, dirty]);

  const filteredTeams = teams
    .filter(team => team.isActive)
    .filter(team => {
      const text = `${team.groupName || ''} ${team.name} ${team.countryCode}`.toLowerCase();
      return text.includes(query.trim().toLowerCase());
    })
    .slice(0, 20);

  return (
    <div className="admin-form-group football-search-select">
      <label>{label}</label>
      <div className="football-search-box">
        <FiSearch />
        <input value={query} onFocus={() => setOpen(true)} onChange={e => { setDirty(true); setQuery(e.target.value); setOpen(true); onChange(''); }} placeholder="搜尋隊伍名稱、組別、代碼" />
      </div>
      {open && <div className="football-search-menu">
        {filteredTeams.map(team => <button type="button" key={team.id} onMouseDown={() => { setDirty(false); onChange(team.id); setQuery(`${team.groupName || ''} ${team.name}`.trim()); setOpen(false); }}><TeamName team={team} /><small>{team.countryCode}</small></button>)}
        {filteredTeams.length === 0 && <div className="football-search-empty">找不到隊伍</div>}
      </div>}
    </div>
  );
}

function TeamsTab({ teams, teamForm, setTeamForm, editingTeamId, setEditingTeamId, saveTeam, editTeam }) {
  return (
    <>
      <form className="football-admin-panel" onSubmit={saveTeam}>
        <h2><FiFlag />{editingTeamId ? '編輯隊伍' : '新增隊伍'}</h2>
        <div className="admin-form-grid">
          <div className="admin-form-group"><label>隊伍名稱</label><input value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} placeholder="例如：美國" /></div>
          <div className="admin-form-group"><label>隊伍代碼</label><input value={teamForm.countryCode} onChange={e => setTeamForm({ ...teamForm, countryCode: e.target.value.toUpperCase() })} placeholder="USA" /></div>
          <div className="admin-form-group"><label>FlagCDN 代碼</label><input value={teamForm.flagCode} onChange={e => setTeamForm({ ...teamForm, flagCode: e.target.value.toLowerCase(), flagUrl: e.target.value ? `https://flagcdn.com/w80/${e.target.value.toLowerCase()}.png` : '' })} placeholder="us" /></div>
          <div className="admin-form-group"><label>國旗圖片 URL</label><input value={teamForm.flagUrl} onChange={e => setTeamForm({ ...teamForm, flagUrl: e.target.value })} placeholder="https://flagcdn.com/w80/us.png" /></div>
          <div className="admin-form-group"><label>組別</label><input value={teamForm.groupName} onChange={e => setTeamForm({ ...teamForm, groupName: e.target.value })} placeholder="A組" /></div>
          <div className="admin-form-group"><label>排序</label><input type="number" value={teamForm.sortOrder} onChange={e => setTeamForm({ ...teamForm, sortOrder: e.target.value })} /></div>
          <label className="admin-checkbox football-admin-check"><input type="checkbox" checked={teamForm.isActive} onChange={e => setTeamForm({ ...teamForm, isActive: e.target.checked })} />啟用</label>
          <div className="football-flag-preview"><FlagImage team={teamForm} /><span>國旗預覽</span></div>
        </div>
        <div className="football-admin-actions">
          <button className="admin-btn-primary" type="submit"><FiPlus />{editingTeamId ? '儲存隊伍' : '新增隊伍'}</button>
          {editingTeamId && <button className="admin-btn-secondary" type="button" onClick={() => { setEditingTeamId(null); setTeamForm(emptyTeam); }}>取消編輯</button>}
        </div>
      </form>

      <section className="football-admin-panel wide">
        <h2><FiUsers />隊伍列表</h2>
        <div className="football-team-groups">
          {groupTeams(teams).map(group => <div className="football-team-group" key={group.groupName}><h3>{group.groupName}</h3>{group.teams.map(team => <button type="button" className="football-team-row" key={team.id} onClick={() => editTeam(team)}><FlagImage team={team} /><span>{team.name}</span><small>{team.countryCode}</small><FiEdit2 /></button>)}</div>)}
        </div>
      </section>
    </>
  );
}

function SettlementTab({ matches, predictions, selectedMatchId, setSelectedMatchId, selectedMatch, settleTeamId, setSettleTeamId, settleMatch }) {
  return (
    <>
      <section className="football-admin-panel wide">
        <h2><FiCheck />賽事結算</h2>
        <div className="football-settle-box">
          <select value={selectedMatchId} onChange={e => setSelectedMatchId(e.target.value)}><option value="">選擇賽事</option>{matches.map(match => <option key={match.id} value={match.id}>{new Date(match.matchAt).toLocaleString('zh-TW')} - {match.teamA.name} vs {match.teamB.name}</option>)}</select>
          {selectedMatch && <select value={settleTeamId} onChange={e => setSettleTeamId(e.target.value)}><option value="">選擇勝利隊伍</option><option value={selectedMatch.teamA.id}>{selectedMatch.teamA.name}</option><option value={selectedMatch.teamB.id}>{selectedMatch.teamB.name}</option></select>}
          <button type="button" className="admin-btn-primary" onClick={settleMatch}><FiCheck />進行結算</button>
        </div>
        {selectedMatch && <div className="football-selected-match"><TeamName team={selectedMatch.teamA} /> <strong>VS</strong> <TeamName team={selectedMatch.teamB} /></div>}
      </section>

      <section className="football-admin-panel wide">
        <h2><FiAward />預測與優惠金結果</h2>
        <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>姓名</th><th>身分證</th><th>手機</th><th>預測隊伍</th><th>勝利隊伍</th><th>結果</th><th>獎項</th><th>優惠金</th><th>時間</th></tr></thead><tbody>{predictions.map(row => <tr key={row.id}><td>{row.playerName}</td><td>{row.nationalId}</td><td>{row.phone}</td><td><TeamName team={row.selectedTeam} /></td><td>{row.winnerTeam ? <TeamName team={row.winnerTeam} /> : '-'}</td><td>{resultText(row.resultStatus)}</td><td>{row.prizeName}</td><td>{money(row.rewardAmount)}</td><td>{new Date(row.createdAt).toLocaleString('zh-TW')}</td></tr>)}</tbody></table></div>
      </section>
    </>
  );
}

function fromMatch(match) {
  const date = new Date(match.matchAt);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return {
    matchDate: date.toISOString().slice(0, 10),
    matchTime: date.toISOString().slice(11, 16),
    teamAId: match.teamA.id,
    teamBId: match.teamB.id,
    teamARewardPerPoint: match.teamARewardPerPoint,
    teamBRewardPerPoint: match.teamBRewardPerPoint,
    status: match.status,
  };
}

function toMatchPayload(form) {
  return {
    matchAt: `${form.matchDate}T${form.matchTime || '00:00'}`,
    teamAId: form.teamAId,
    teamBId: form.teamBId,
    teamARewardPerPoint: form.teamARewardPerPoint,
    teamBRewardPerPoint: form.teamBRewardPerPoint,
    status: form.status,
  };
}

function TeamName({ team }) {
  return <span className="football-team-name"><FlagImage team={team} />{team.name}</span>;
}

function FlagImage({ team }) {
  if (team?.flagUrl) return <img className="football-flag-img" src={team.flagUrl} alt={team.name ? `${team.name}國旗` : '國旗'} loading="lazy" />;
  return <span className="football-flag-fallback">{team?.flagEmoji || '🏳️'}</span>;
}

function groupTeams(teams) {
  const grouped = teams.reduce((acc, team) => {
    const groupName = team.groupName || '未分組';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(team);
    return acc;
  }, {});
  return Object.entries(grouped).map(([groupName, groupTeams]) => ({ groupName, teams: groupTeams }));
}

function money(value) {
  return Number(value || 0).toLocaleString('zh-TW', { maximumFractionDigits: 2 });
}

function statusText(status) {
  return { open: '開放', closed: '關閉', settled: '已結算' }[status] || status;
}

function resultText(status) {
  return { pending: '待結算', won: '中獎', lost: '未中' }[status] || status;
}

export default AdminFootballPage;
